import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, BookOpen, ArrowRight, RefreshCw, Clock, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { getReadingsCache, saveReadingsCache } from "../lib/supabase";
import { supabase } from "../lib/supabase";

// Mapping AELF book names to our database IDs (including abbreviations)
const bookNameMapping = {
  // Ancien Testament - Abbréviations
  'gn': 'genese', 'ex': 'exode', 'lv': 'levitique', 'nb': 'nombres', 'dt': 'deuteronome',
  'jos': 'josue', 'jg': 'juges', 'rt': 'ruth',
  '1s': 'samuel1', '2s': 'samuel2', '1r': 'rois1', '2r': 'rois2',
  '1ch': 'chroniques1', '2ch': 'chroniques2',
  'esd': 'esdras', 'ne': 'nehemie', 'tb': 'tobie', 'jdt': 'judith', 'est': 'esther',
  '1m': 'maccabees1', '2m': 'maccabees2',
  'jb': 'job', 'ps': 'psaumes', 'pr': 'proverbes', 'qo': 'ecclesiaste', 'ct': 'cantique',
  'sg': 'sagesse', 'si': 'siracide',
  'is': 'isaie', 'jr': 'jeremie', 'lm': 'lamentations', 'ba': 'baruch',
  'ez': 'ezechiel', 'dn': 'daniel',
  'os': 'osee', 'jl': 'joel', 'am': 'amos', 'ab': 'abdias', 'jon': 'jonas',
  'mi': 'michee', 'na': 'nahum', 'ha': 'habacuc', 'so': 'sophonie',
  'ag': 'aggee', 'za': 'zacharie', 'ml': 'malachie',
  
  // Nouveau Testament - Abbréviations
  'mt': 'matthieu', 'mc': 'marc', 'lc': 'luc', 'jn': 'jean',
  'ac': 'actes',
  'rm': 'romains', '1co': 'corinthiens1', '2co': 'corinthiens2',
  'ga': 'galates', 'ep': 'ephesiens', 'ph': 'philippiens', 'col': 'colossiens',
  '1th': 'thessaloniciens1', '2th': 'thessaloniciens2',
  '1tm': 'timothee1', '2tm': 'timothee2', 'tt': 'tite', 'phm': 'philemon',
  'he': 'hebreux', 'jc': 'jacques',
  '1p': 'pierre1', '2p': 'pierre2',
  '1jn': 'jean1', '2jn': 'jean2', '3jn': 'jean3',
  'jude': 'jude', 'ap': 'apocalypse',
  
  // Noms complets
  'genèse': 'genese', 'génèse': 'genese',
  'exode': 'exode',
  'lévitique': 'levitique', 'levitique': 'levitique',
  'nombres': 'nombres',
  'deutéronome': 'deuteronome',
  'josué': 'josue', 'josue': 'josue',
  'juges': 'juges',
  'ruth': 'ruth',
  '1 samuel': 'samuel1', 'premier livre de samuel': 'samuel1',
  '2 samuel': 'samuel2', 'deuxième livre de samuel': 'samuel2',
  '1 rois': 'rois1', 'premier livre des rois': 'rois1',
  '2 rois': 'rois2', 'deuxième livre des rois': 'rois2',
  '1 chroniques': 'chroniques1', 'premier livre des chroniques': 'chroniques1',
  '2 chroniques': 'chroniques2', 'deuxième livre des chroniques': 'chroniques2',
  'esdras': 'esdras',
  'néhémie': 'nehemie', 'nehemie': 'nehemie',
  'tobie': 'tobie',
  'judith': 'judith',
  'esther': 'esther',
  '1 maccabées': 'maccabees1', '1 maccabees': 'maccabees1',
  '2 maccabées': 'maccabees2', '2 maccabees': 'maccabees2',
  'job': 'job',
  'psaume': 'psaumes', 'psaumes': 'psaumes',
  'proverbes': 'proverbes',
  'ecclésiaste': 'ecclesiaste', 'qohélet': 'ecclesiaste',
  'cantique': 'cantique', 'cantique des cantiques': 'cantique',
  'sagesse': 'sagesse',
  'siracide': 'siracide', 'ecclésiastique': 'siracide',
  'isaïe': 'isaie', 'isaie': 'isaie',
  'jérémie': 'jeremie', 'jeremie': 'jeremie',
  'lamentations': 'lamentations',
  'baruch': 'baruch',
  'ézéchiel': 'ezechiel', 'ezechiel': 'ezechiel',
  'daniel': 'daniel',
  'osée': 'osee', 'osee': 'osee',
  'joël': 'joel', 'joel': 'joel',
  'amos': 'amos',
  'abdias': 'abdias',
  'jonas': 'jonas',
  'michée': 'michee', 'michee': 'michee',
  'nahum': 'nahum',
  'habacuc': 'habacuc',
  'sophonie': 'sophonie',
  'aggée': 'aggee', 'aggee': 'aggee',
  'zacharie': 'zacharie',
  'malachie': 'malachie',
  'matthieu': 'matthieu', 'évangile selon saint matthieu': 'matthieu',
  'marc': 'marc', 'évangile selon saint marc': 'marc',
  'luc': 'luc', 'évangile selon saint luc': 'luc',
  'jean': 'jean', 'évangile selon saint jean': 'jean',
  'actes': 'actes', 'actes des apôtres': 'actes',
  'romains': 'romains', 'lettre de saint paul aux romains': 'romains',
  '1 corinthiens': 'corinthiens1', 'première lettre de saint paul aux corinthiens': 'corinthiens1',
  '2 corinthiens': 'corinthiens2', 'deuxième lettre de saint paul aux corinthiens': 'corinthiens2',
  'galates': 'galates', 'lettre de saint paul aux galates': 'galates',
  'éphésiens': 'ephesiens', 'ephesiens': 'ephesiens', 'lettre de saint paul aux éphésiens': 'ephesiens',
  'philippiens': 'philippiens', 'lettre de saint paul aux philippiens': 'philippiens',
  'colossiens': 'colossiens', 'lettre de saint paul aux colossiens': 'colossiens',
  '1 thessaloniciens': 'thessaloniciens1', 'première lettre de saint paul aux thessaloniciens': 'thessaloniciens1',
  '2 thessaloniciens': 'thessaloniciens2', 'deuxième lettre de saint paul aux thessaloniciens': 'thessaloniciens2',
  '1 timothée': 'timothee1', 'première lettre de saint paul à timothée': 'timothee1',
  '2 timothée': 'timothee2', 'deuxième lettre de saint paul à timothée': 'timothee2',
  'tite': 'tite', 'lettre de saint paul à tite': 'tite',
  'philémon': 'philemon', 'lettre de saint paul à philémon': 'philemon',
  'hébreux': 'hebreux', 'hebreux': 'hebreux', 'lettre aux hébreux': 'hebreux',
  'jacques': 'jacques', 'lettre de saint jacques': 'jacques',
  '1 pierre': 'pierre1', 'première lettre de saint pierre': 'pierre1',
  '2 pierre': 'pierre2', 'deuxième lettre de saint pierre': 'pierre2',
  '1 jean': 'jean1', 'première lettre de saint jean': 'jean1',
  '2 jean': 'jean2', 'deuxième lettre de saint jean': 'jean2',
  '3 jean': 'jean3', 'troisième lettre de saint jean': 'jean3',
  'jude': 'jude', 'lettre de saint jude': 'jude',
  'apocalypse': 'apocalypse'
};

const ReadingsPage = () => {
  const [readings, setReadings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextUpdate, setNextUpdate] = useState(null);

  // Calculate time until next 5 AM update
  const calculateNextUpdate = useCallback(() => {
    const now = new Date();
    const next5AM = new Date(now);
    next5AM.setHours(5, 0, 0, 0);
    
    if (now >= next5AM) {
      next5AM.setDate(next5AM.getDate() + 1);
    }
    
    return next5AM;
  }, []);

  const formatTimeRemaining = (targetDate) => {
    const now = new Date();
    const diff = targetDate - now;
    
    if (diff <= 0) return "Mise à jour disponible";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `dans ${hours}h ${minutes}min`;
    }
    return `dans ${minutes} minutes`;
  };

  // Parse reference like "Genèse 1, 1-5" or "Matthieu 5, 1-12" 
  const parseReference = (ref) => {
    if (!ref) return null;
    
    try {
      // Clean up reference
      const cleaned = ref.trim().toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/–/g, '-') // Replace en-dash with hyphen
        .replace(/\u00a0/g, ' '); // Replace non-breaking space
      
      console.log('📖 Parsing reference:', ref, '→', cleaned);
      
      // Try to extract book name and chapter/verses
      // Pattern: "book chapter, verse" or "book chapter, verse1-verse2"
      const match = cleaned.match(/^(.+?)\s+(\d+)\s*,\s*(\d+)(?:\s*-\s*(\d+))?/);
      
      if (!match) {
        console.warn('❌ No match for reference:', cleaned);
        return null;
      }
      
      const bookName = match[1].trim();
      const chapter = parseInt(match[2]);
      const verseStart = parseInt(match[3]);
      const verseEnd = match[4] ? parseInt(match[4]) : verseStart;
      
      console.log('🔍 Extracted:', { bookName, chapter, verseStart, verseEnd });
      
      // Find book ID
      const bookId = bookNameMapping[bookName];
      
      if (!bookId) {
        console.warn(`❌ Book not found in mapping: "${bookName}" from ref: "${ref}"`);
        console.warn(`⚠️ ABRÉVIATION MANQUANTE - Merci de signaler: "${bookName}" → devrait correspondre à quel livre ?`);
        return null;
      }
      
      console.log('✅ Matched book ID:', bookId);
      
      return {
        bookId,
        chapter,
        verseStart,
        verseEnd
      };
    } catch (e) {
      console.error('❌ Error parsing reference:', ref, e);
      return null;
    }
  };

  // Fetch verses from our Supabase database
  const fetchVersesFromDB = async (bookId, chapter, verseStart, verseEnd) => {
    try {
      console.log(`📚 Fetching verses: ${bookId} ${chapter}:${verseStart}-${verseEnd}`);
      
      const { data, error } = await supabase
        .from('bible_verses')
        .select('verse_number, french')
        .eq('book_id', bookId)
        .eq('chapter_number', chapter)
        .gte('verse_number', verseStart)
        .lte('verse_number', verseEnd)
        .order('verse_number', { ascending: true });
      
      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.warn(`⚠️ No verses found for ${bookId} ${chapter}:${verseStart}-${verseEnd}`);
        return null;
      }
      
      console.log(`✅ Found ${data.length} verses from Fillion`);
      
      // Format verses with numbers
      return data.map(v => `<sup>${v.verse_number}</sup> ${v.french}`).join(' ');
    } catch (e) {
      console.error('❌ Error fetching verses:', e);
      return null;
    }
  };

  // Fetch readings from AELF
  const fetchFromAELF = async () => {
    const today = new Date().toISOString().split('T')[0];
    
    // Map AELF types to French labels
    const typeLabels = {
      'lecture_1': 'Première lecture',
      'lecture_2': 'Deuxième lecture',
      'psaume': 'Psaume',
      'evangile': 'Évangile',
      'sequence': 'Séquence'
    };
    
    try {
      // Use AELF API directly
      const response = await fetch(`https://api.aelf.org/v1/messes/${today}/france`);
      if (!response.ok) throw new Error('AELF API error');
      
      const data = await response.json();
      
      // Transform AELF data
      const liturgy = data.informations?.jour_liturgique_nom || '';
      const readingsData = [];
      
      // Extract lectures from messes array
      if (data.messes && data.messes.length > 0) {
        const messe = data.messes[0];
        if (messe.lectures) {
          for (const lecture of messe.lectures) {
            const reference = lecture.ref || '';
            const parsed = parseReference(reference);
            
            let text = lecture.contenu || '';
            let useFillion = false;
            
            // Try to get text from our database if we have a valid reference
            if (parsed && parsed.bookId) {
              const fillionText = await fetchVersesFromDB(
                parsed.bookId, 
                parsed.chapter, 
                parsed.verseStart, 
                parsed.verseEnd
              );
              
              if (fillionText) {
                text = fillionText;
                useFillion = true;
              }
            }
            
            readingsData.push({
              type: typeLabels[lecture.type] || lecture.type || 'Lecture',
              title: lecture.titre || '',
              reference: reference,
              text: text,
              refrain: lecture.refrain_psalmique || '',
              book_id: parsed?.bookId || null,
              chapter: parsed?.chapter || null,
              useFillion: useFillion
            });
          }
        }
      }
      
      return {
        date: today,
        liturgy,
        readings: readingsData
      };
    } catch (err) {
      console.error('AELF fetch error:', err);
      throw err;
    }
  };

  const fetchReadings = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    const today = new Date().toISOString().split('T')[0];
    
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        try {
          const cached = await getReadingsCache(today);
          if (cached && cached.readings_json) {
            setReadings({
              date: cached.date,
              liturgy: cached.liturgy,
              readings: cached.readings_json
            });
            setNextUpdate(calculateNextUpdate());
            setLoading(false);
            return;
          }
        } catch (cacheErr) {
          console.log('No cache found, fetching from AELF');
        }
      }
      
      // Fetch from AELF
      const data = await fetchFromAELF();
      setReadings(data);
      setNextUpdate(calculateNextUpdate());
      
      // Save to cache
      try {
        await saveReadingsCache(today, data.liturgy, data.readings);
      } catch (saveErr) {
        console.warn('Could not save to cache:', saveErr);
      }
      
    } catch (err) {
      console.error("Error fetching readings:", err);
      setError("Impossible de charger les lectures du jour. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateNextUpdate]);

  useEffect(() => {
    fetchReadings();
  }, [fetchReadings]);

  useEffect(() => {
    const checkForUpdate = () => {
      const now = new Date();
      if (now.getHours() === 5 && now.getMinutes() === 0) {
        fetchReadings(true);
      }
      setNextUpdate(calculateNextUpdate());
    };

    const interval = setInterval(checkForUpdate, 60000);
    return () => clearInterval(interval);
  }, [fetchReadings, calculateNextUpdate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTypeColor = (type) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('évangile') || t.includes('evangile')) return 'text-rubric-red';
    if (t.includes('psaume')) return 'text-gold-leaf';
    return 'text-ink-primary';
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] bg-paper flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-ink-secondary animate-spin mx-auto mb-4" />
          <p className="font-body text-ink-secondary italic">Chargement des lectures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] bg-paper flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-amber-600 mx-auto mb-4" />
          <p className="font-body text-ink-secondary mb-4">{error}</p>
          <Button onClick={() => fetchReadings(true)} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper animate-fade-in">
      <div className="container mx-auto px-4 py-6 sm:py-12 max-w-3xl">
        {/* Header */}
        <header className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="h-6 w-6 text-rubric-red" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl text-ink-primary">
              Lectures du jour
            </h1>
          </div>
          
          {readings && (
            <>
              <p className="font-body text-base sm:text-lg text-ink-secondary capitalize">
                {formatDate(readings.date)}
              </p>
              {readings.liturgy && (
                <p className="font-body text-ink-secondary italic mt-2 text-sm sm:text-base px-4">
                  {readings.liturgy}
                </p>
              )}
            </>
          )}

          {nextUpdate && (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs sm:text-sm text-ink-secondary">
              <Clock className="h-3.5 w-3.5" />
              <span>Prochaine mise à jour à 5h00 ({formatTimeRemaining(nextUpdate)})</span>
            </div>
          )}
          
          <div className="mt-6 sm:mt-8 flex items-center justify-center">
            <span className="font-decorative text-gold-leaf px-4">✦</span>
          </div>
        </header>

        {/* Readings list */}
        <div className="space-y-6 sm:space-y-8">
          {readings?.readings?.map((reading, index) => (
            <Card 
              key={index} 
              className="bg-paper-dark border-border overflow-hidden"
            >
              <CardHeader className="pb-3 px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1">
                    <CardTitle className={`font-heading text-lg sm:text-xl ${getReadingTypeColor(reading.type)}`}>
                      {reading.type}
                    </CardTitle>
                    {reading.title && (
                      <p className="font-body text-xs sm:text-sm text-ink-secondary mt-1 italic">
                        {reading.title}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-start sm:items-end gap-1">
                    {reading.reference && (
                      <span className="text-sm text-ink-secondary font-body">
                        {reading.reference}
                      </span>
                    )}
                    {reading.useFillion && (
                      <span className="text-xs bg-gold-leaf/10 text-gold-leaf px-2 py-0.5 rounded">
                        Traduction Fillion 1920
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <Separator className="bg-border" />
              
              <CardContent className="pt-4 px-4 sm:px-6">
                <div 
                  className="font-body text-ink-primary leading-relaxed text-sm sm:text-base prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: reading.text || '' }}
                />
              </CardContent>
            </Card>
          ))}
        </div>

        {(!readings?.readings || readings.readings.length === 0) && (
          <div className="text-center py-12">
            <p className="text-ink-secondary">Aucune lecture disponible pour aujourd'hui.</p>
          </div>
        )}

        {/* Bottom actions */}
        <div className="mt-8 sm:mt-12 text-center">
          <Button
            onClick={() => fetchReadings(true)}
            variant="outline"
            className="text-ink-primary hover:text-rubric-red border-border hover:border-rubric-red"
          >
            <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Forcer l'actualisation
          </Button>
        </div>

        {/* Note */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="font-body text-xs sm:text-sm text-ink-secondary italic px-4">
            Lectures issues de l'AELF (Association Épiscopale Liturgique pour les pays Francophones)
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReadingsPage;
